"use client";

import { useDashboardRouter } from "@/hooks/useDashboardRouter";
import { proofReportArtifactId } from "../proof/proofReportArtifactId";
import type { ProofFinding } from "../proof/proofRun";
import type { ProofIndexEntry } from "./proofIndexEntry";

function useOrgPrefix(): string {
  const router = useDashboardRouter();
  const org = typeof router.query.org === "string" ? router.query.org : null;
  return org ? `/${org}` : "";
}

/**
 * Plain link to the Proof child session, not useSessionLink's shallow push:
 * that in-shell switch only applies inside the codesmith shell, and the Proof
 * index lives outside it.
 */
export function useProofSessionHref(sessionId: string): string {
  return `${useOrgPrefix()}/sessions/${sessionId}`;
}

/**
 * The session the Proof was started from, with its report open in the
 * workspace pane, else the Proof's own session: parent `ses_p`, proof `ses_1`
 * → `/sessions/ses_p?pane=proof-report%3Ases_1&focus=proof`.
 */
export function proofReportSessionPath(
  entry: Pick<ProofIndexEntry, "sessionId" | "parentSessionId">,
): string {
  if (!entry.parentSessionId) return `/sessions/${entry.sessionId}`;
  const query = new URLSearchParams({
    pane: proofReportArtifactId(entry.sessionId),
    focus: "proof",
  });
  return `/sessions/${entry.parentSessionId}?${query}`;
}

export function useProofReportSessionHref(
  entry: Pick<ProofIndexEntry, "sessionId" | "parentSessionId">,
): string {
  return `${useOrgPrefix()}${proofReportSessionPath(entry)}`;
}

/**
 * New-session draft asking codesmith to fix the run's issue findings, with
 * the PR linked the way "View in Codesmith" links one (`?repo=&pr=`, see
 * useDraftUrlContext).
 */
export function useProofFixSessionHref(
  entry: Pick<ProofIndexEntry, "repository" | "prNumber">,
  issues: Pick<ProofFinding, "title" | "observed">[],
): string {
  const prefix = useOrgPrefix();
  const prompt = [
    `Proof found ${issues.length === 1 ? "an issue" : `${issues.length} issues`} reviewing ${entry.repository}#${entry.prNumber}:`,
    ...issues.map((issue) => `- ${issue.title}: ${issue.observed}`),
    "",
    `Fix ${issues.length === 1 ? "it" : "them"} on the PR's branch.`,
  ].join("\n");
  const query = new URLSearchParams({
    prompt,
    repo: entry.repository,
    pr: String(entry.prNumber),
  });
  return `${prefix}/sessions/new?${query}`;
}

/** New-session draft asking codesmith to run Proof again on the PR's latest commit. */
export function useProofRunAgainSessionHref(
  entry: Pick<ProofIndexEntry, "repository" | "prNumber">,
): string {
  const prefix = useOrgPrefix();
  const query = new URLSearchParams({
    prompt: `Run Proof again on ${entry.repository}#${entry.prNumber} at its latest commit.`,
    repo: entry.repository,
    pr: String(entry.prNumber),
  });
  return `${prefix}/sessions/new?${query}`;
}
